import {
  appModelOverrideKey,
  parseAiFeatureOverride,
  type AiConsumerTier,
  type AiFeatureOverride,
  type ListAppRegistryResponse
} from '@bliks-io/contracts';

type RegistryApp = ListAppRegistryResponse['apps'][number];

/** Pending edits keyed by raw key; `null` = cleared, absent = untouched. */
export type PendingEdits<T> = Record<string, T | null>;
export type StoredValues<T> = Partial<Record<string, T>>;

export interface AppModelRow {
  key: string;
  label: string;
  /**
   * Set on slot rows: the app-wide key a cleared slot falls back to. Absent on
   * a decision slot, which follows only its own override, as
   * `getDecisionModelForAppSlot` does.
   */
  inheritsFrom?: string;
  defaultTier: AiConsumerTier;
}

export interface AppModelGroup {
  appId: string;
  label: string;
  /** Absent when the app left the registry or stopped declaring a text tier. */
  appRow?: AppModelRow;
  slotRows: AppModelRow[];
  /** Stored app-level key no declared row covers — only on appRow-less groups. */
  appOrphanKey?: string;
  /** Stored slot keys no declared row covers: a renamed or removed slot. */
  orphanSlots: { key: string; label: string }[];
}

/**
 * One group per app that declares a text tier: the app-wide row plus one row
 * per declared model slot. Stored keys no row covers (a renamed slot, an app
 * that left the registry) attach as orphans under their appId prefix —
 * without a row of their own they are invisible and unclearable from the UI,
 * and they take effect again, silently, the moment the key comes back. A key
 * counts as stored when any of the given maps holds it.
 */
export function deriveAppModelGroups(
  apps: RegistryApp[],
  ...stored: StoredValues<unknown>[]
): AppModelGroup[] {
  const groups: AppModelGroup[] = apps.flatMap((app) => {
    const defaultTier = app.ai?.text;
    if (!defaultTier) return [];

    const appKey = appModelOverrideKey(app.appId);
    return [
      {
        appId: app.appId,
        label: app.name,
        appRow: { key: appKey, label: app.name, defaultTier },
        slotRows: Object.entries(app.ai?.slots ?? {}).map(([slot, tier]) => ({
          key: appModelOverrideKey(app.appId, slot),
          label: slot,
          ...(tier === 'decision' ? {} : { inheritsFrom: appKey }),
          defaultTier: tier
        })),
        orphanSlots: []
      }
    ];
  });

  // The app-wide key equals the appId, so group.appId covers it.
  const declared = new Set(
    groups.flatMap((group) => [group.appId, ...group.slotRows.map((row) => row.key)])
  );
  const byAppId = new Map(groups.map((group) => [group.appId, group]));
  const orphanKeys = [...new Set(stored.flatMap((map) => Object.keys(map)))]
    .filter((key) => !declared.has(key))
    .sort();
  for (const key of orphanKeys) {
    const colon = key.indexOf(':');
    const appId = colon === -1 ? key : key.slice(0, colon);
    let group = byAppId.get(appId);
    if (!group) {
      group = { appId, label: appId, slotRows: [], orphanSlots: [] };
      byAppId.set(appId, group);
      groups.push(group);
    }
    if (colon === -1) group.appOrphanKey = key;
    else group.orphanSlots.push({ key, label: key.slice(colon + 1) });
  }
  return groups;
}

// Override keys are open strings, so an app id like `constructor` would
// otherwise resolve against Object.prototype instead of the map.
const ownValue = <T>(map: Partial<Record<string, T>>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

/** The edited-or-saved value of a key; a pending `null` reads as "not set". */
export function pendingOrStored<T>(
  key: string,
  pending: PendingEdits<T>,
  stored: StoredValues<T>
): T | undefined {
  const edited = ownValue(pending, key);
  return edited !== undefined ? (edited ?? undefined) : ownValue(stored, key);
}

/** What a row falls back to when cleared — a slot follows its app's pending edit. */
export function inheritedValue(
  row: AppModelRow,
  pending: PendingEdits<AiFeatureOverride>,
  stored: StoredValues<AiFeatureOverride>
): AiFeatureOverride | AiConsumerTier {
  if (!row.inheritsFrom) return row.defaultTier;
  // `getModelForApp` skips a candidate that no longer parses, so a stale
  // app-level value resolves the slot from its default tier on the server too.
  const inherited = pendingOrStored(row.inheritsFrom, pending, stored);
  return parseAiFeatureOverride(inherited) ?? row.defaultTier;
}

/**
 * Raw-to-raw diff: never compare resolved values, or clearing a slot key that a
 * shadowing app-wide override makes a no-op would skip its `$unset`.
 */
export function diffPendingEdits<T>(
  pending: PendingEdits<T>,
  stored: StoredValues<T>
): PendingEdits<T> {
  const changed: PendingEdits<T> = {};
  for (const [key, value] of Object.entries(pending)) {
    const storedValue = ownValue(stored, key);
    if (value === null) {
      if (storedValue !== undefined) changed[key] = null;
    } else if (value !== storedValue) {
      changed[key] = value;
    }
  }
  return changed;
}
